// Trip API service
// This file contains API calls for trip management

import { getTokensFromCache } from '../../auth/utils/tokenCache';
import { filterTripRecords } from '../utils/tripUtils';

export interface TripDates {
  type: 'concrete' | 'flexible';
  startDate: string; // YYYY-MM-DD format required by backend
  endDate: string; // YYYY-MM-DD format required by backend
  startOffsetDaysPositive?: number;
  startOffsetDaysNegative?: number;
  endOffsetDaysPositive?: number;
  endOffsetDaysNegative?: number;
}

export interface TripTravelers {
  adults: number;
  children: number;
}

export interface Trip {
  id: string | number;
  title: string;
  destinations: string[];
  dates: TripDates;
  status: string;
  travelers?: TripTravelers;
  images?: string[];
  activities?: string[];
  budget?: string | number | { amount: number; currency: string };
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  progress?: number;
  rating?: number;
  memories?: number;
  conversation_id?: string;
  // Reactions enrichment (T2.14/D-25): the server merges the public like count
  // and THIS viewer's own state onto every trip read — one request per screen.
  // Bookmarks are private, so there is deliberately no save count here.
  likes?: number;
  viewer?: { liked: boolean; saved: boolean };
  // T2.3 (D-35): the caller's relationship to this trip + its visibility.
  viewerRole?: 'owner' | 'collaborator' | 'member' | 'buyer' | 'follower-viewer' | 'public-viewer';
  visibility?: 'private' | 'followers' | 'public'; // explicit override only
  effectiveVisibility?: 'private' | 'followers' | 'public';
  budgetTier?: 'budget' | 'moderate' | 'premium'; // limited-DTO replacement for amounts
}

export type TripVisibilityChoice = 'default' | 'private' | 'followers' | 'public';

export interface TripMemberDto {
  userId: string;
  role: 'collaborator' | 'member';
  createdAt?: string;
}

export interface CreateTripRequest {
  title: string;
  destinations: string[];
  dates: TripDates;
  status: 'draft' | 'planning' | 'in_progress' | 'completed'; // Must match backend TripStatus enum
  travelMode?: 'drive' | 'walk' | 'transit'; // trip-level default for schedule recompute (T1.16)
  images?: string[];
  activities?: string[];
  budget?: {
    amount: number;
    currency: string;
  };
  travelers?: TripTravelers;
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  conversation_id?: string;
}

export interface UpdateTripRequest extends Partial<CreateTripRequest> {
  id: string;
}

// UI-facing activity "view" shape. The trips server persists a richer contract
// ({ startTime, endTime, location: {type, name/place_id}, cost }); the adapter
// functions below translate at the API boundary so every screen keeps consuming
// this flat shape. `dateTime` = server startTime, `endDateTime` = server endTime.
// The travel leg INTO a stop from the previous one that day (T1.16). Computed
// server-side (Google, mode-aware, auto-walk short hops); `chosenMode` is the
// user's explicit per-leg mode when they've overridden it.
export interface ActivityTravelLeg {
  mode: string;             // 'drive' | 'walk' | 'transit'
  minutes: number | null;   // travel time; null = no route ("can't calculate")
  km: number | null;        // distance
  chosenMode?: string;
  crossDay?: boolean;       // advisory leg across a day boundary
  fromLabel?: string;       // origin stop's name (cross-day INbound leg: "from X")
  toLabel?: string;         // destination stop's name (end-of-day OUTbound leg: "to X")
  warning?: 'far' | 'detour'; // server route-sanity flag (very far / big detour off the corridor)
}

export interface Activity {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  location: string;      // display name / address (server location.name)
  placeId?: string;      // Google place id (server location.place_id), when known
  dateTime?: string;     // ISO 8601 — server startTime
  endDateTime?: string;  // ISO 8601 — server endTime
  budget?: {
    amount: number;
    currency: string;
  };
  visitMinutes?: number;            // dwell time (T1.16)
  travelFromPrev?: ActivityTravelLeg; // inbound travel leg (T1.16)
  status: 'scheduled' | 'planned';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateActivityRequest {
  title: string;
  description?: string;
  location?: string;     // display name / address → server location {type:'text'}
  placeId?: string;      // Google place id → server location {type:'place_id'}
  dateTime?: string;     // ISO 8601 → server startTime
  endDateTime?: string;  // ISO 8601 → server endTime
  budget?: {
    amount: number;
    currency: string;
  };
}

export interface UpdateActivityRequest extends Partial<CreateActivityRequest> {
  id: string;
}

// ── Activity wire adapters (UI view ⇄ trips-server contract) ──────────────────

// Server activity record → UI Activity view. Tolerant of either contract so a
// stale response shape never blanks the screen.
const fromServerActivity = (raw: any): Activity => {
  const loc = raw?.location;
  const location = typeof loc === 'string' ? loc : (loc?.name || loc?.place_id || '');
  const placeId = loc && typeof loc === 'object' && loc.type === 'place_id' ? loc.place_id : undefined;
  const startTime = raw?.startTime || raw?.dateTime;
  return {
    id: raw?.id,
    tripId: raw?.tripId,
    title: raw?.title,
    description: raw?.description,
    location,
    placeId,
    dateTime: startTime,
    endDateTime: raw?.endTime,
    budget: raw?.cost || raw?.budget,
    visitMinutes: typeof raw?.visitMinutes === 'number' ? raw.visitMinutes : undefined,
    travelFromPrev: raw?.travelFromPrev || undefined,
    status: raw?.status || (startTime ? 'scheduled' : 'planned'),
    createdAt: raw?.createdAt,
    updatedAt: raw?.updatedAt,
  };
};

// UI create/update request → server activity body. Builds the structured
// location object and start/end times the server validates.
const toServerActivityBody = (req: Partial<CreateActivityRequest>): Record<string, any> => {
  const body: Record<string, any> = {};
  if (req.title !== undefined) body.title = req.title;
  if (req.description !== undefined) body.description = req.description;
  if (req.placeId) {
    body.location = { type: 'place_id', place_id: req.placeId, ...(req.location ? { name: req.location } : {}) };
  } else if (req.location) {
    body.location = { type: 'text', name: req.location };
  }
  if (req.dateTime) body.startTime = req.dateTime;
  // The server rejects endTime <= startTime; only send a strictly-later end.
  if (req.endDateTime && (!req.dateTime || new Date(req.endDateTime) > new Date(req.dateTime))) {
    body.endTime = req.endDateTime;
  }
  if (req.budget) body.cost = req.budget;
  return body;
};

export type TravelMode = 'drive' | 'walk' | 'transit';

const API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

// Get access token from cache using proper token cache utility
const getAccessToken = (): string | null => {
  try {
    const tokens = getTokensFromCache();
    return tokens?.accessToken || null;
  } catch {
    return null;
  }
};

export const getTrips = async (): Promise<Trip[]> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const url = `${API_ENDPOINT}/trips`;
  console.log('🌐 getTrips API call:', { url });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // No Bearer prefix - API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTrips failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch trips: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ getTrips response:', data);
  console.log('✅ Trips array:', data.trips);
  console.log('✅ Trip IDs from backend:', (data.trips || []).map((t: Trip) => ({ id: t.id, title: t.title })));
  return filterTripRecords(data.trips || []);
};

export const getTripById = async (tripId: string | number): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 getTripById API call:', { url, tripId: tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTripById failed:', { status: response.status, statusText: response.statusText, errorText, tripId });
    throw new Error(`Failed to fetch trip: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ getTripById response:', data);
  return data.trip;
};


// Small authorized-JSON helper for the roster/visibility endpoints.
const authorizedFetch = async (path: string, init?: RequestInit): Promise<any> => {
  if (!API_ENDPOINT) throw new Error('API Gateway endpoint not configured');
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('User not authenticated');
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: accessToken, ...(init?.headers || {}) },
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const error: any = new Error(`Request failed (${response.status}) ${detail}`.trim());
    error.status = response.status;
    throw error;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

// ── T2.3 roster + visibility (share modal) ───────────────────────────────────

export const getTripMembers = async (tripId: string): Promise<{ ownerId: string; members: TripMemberDto[] }> => {
  const response = await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members`);
  return { ownerId: response.ownerId, members: response.members || [] };
};

export const addTripMember = async (
  tripId: string,
  userId: string,
  role: 'collaborator' | 'member',
): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
};

export const removeTripMember = async (tripId: string, userId: string): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
};

export const setTripVisibility = async (
  tripId: string,
  visibility: TripVisibilityChoice,
): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}`, {
    method: 'PUT',
    body: JSON.stringify({ visibility }),
  });
};

export const createTrip = async (tripData: CreateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${API_ENDPOINT}/trip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    // Surface the server's validation message (e.g. "destinations must be an
    // array of strings") instead of the opaque HTTP status.
    const detail = await response.text().catch(() => '');
    let message: string = response.statusText;
    try {
      const parsed = JSON.parse(detail);
      message = parsed.message || parsed.error || message;
    } catch {
      if (detail) message = detail;
    }
    throw new Error(`Failed to create trip: ${message}`);
  }

  const data = await response.json();
  return data.trip;
};

export const updateTrip = async (tripData: UpdateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripData.id);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 updateTrip API call:', { url, tripId: tripData.id });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update trip: ${response.statusText}`);
  }

  const data = await response.json();
  return data.trip;
};

export const deleteTrip = async (tripId: string | number): Promise<void> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 deleteTrip API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ deleteTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to delete trip: ${response.statusText}`);
  }

  console.log('✅ Trip deleted successfully');
};

// Activity API functions

export const getActivities = async (tripId: string): Promise<Activity[]> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activities`;
  console.log('🌐 getActivities API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getActivities failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch activities: ${response.statusText}`);
  }

  const data = await response.json();
  return (data.activities || []).map(fromServerActivity);
};

export const createActivity = async (tripId: string, activityData: CreateActivityRequest): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity`;
  // Translate to the server contract (structured location, start/end times);
  // the server derives status from whether times are present.
  const body = toServerActivityBody(activityData);
  console.log('🌐 createActivity API call:', { url, tripId, body });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ createActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to create activity: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerActivity(data.activity);
};

export const updateActivity = async (tripId: string, activityId: string, activityData: Partial<CreateActivityRequest>): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId and activityId (UUIDs) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const encodedActivityId = encodeURIComponent(activityId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity/${encodedActivityId}`;
  const body = toServerActivityBody(activityData);
  console.log('🌐 updateActivity API call:', { url, tripId, activityId, body });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update activity: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerActivity(data.activity);
};

// Set the transport mode for a stop's INBOUND leg (T1.16). The server stamps
// travelFromPrev.chosenMode, refetches that leg from Google in the chosen mode,
// and re-times the day — so callers should reload the day's activities after.
export const setActivityTravelMode = async (
  tripId: string,
  activityId: string,
  mode: TravelMode,
): Promise<void> => {
  if (!API_ENDPOINT) throw new Error('API Gateway endpoint not configured');
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('User not authenticated');

  const url = `${API_ENDPOINT}/trip/${encodeURIComponent(tripId)}/activity/${encodeURIComponent(activityId)}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': accessToken },
    body: JSON.stringify({ travelMode: mode }),
  });
  if (!response.ok) {
    throw new Error(`Failed to set travel mode (${response.status})`);
  }
};

export const deleteActivity = async (tripId: string, activityId: string): Promise<void> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const url = `${API_ENDPOINT}/trip/${encodeURIComponent(tripId)}/activity/${encodeURIComponent(activityId)}`;
  console.log('🌐 deleteActivity API call:', { url, tripId, activityId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ deleteActivity failed:', { status: response.status, errorText });
    throw new Error(`Failed to delete activity: ${response.statusText}`);
  }

  console.log('✅ Activity deleted successfully');
};
